import { useEffect, useMemo, useRef, useState } from 'react'
import { Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import type { AccountingProduct, FiscalMemoryImport, XmlMemoryLinkResult, XmlMemoryPreview } from './accountingFiscalMemoryTypes'
import type { DocumentUploadProgress, StoredDocument } from '@/modules/documents'
import {
  discardFiscalMemoryUpload,
  linkXmlMemory,
  prepareXmlMemoryPreview,
  uploadXmlMemoryDocuments,
  xmlDocumentsForPeriod,
} from './accountingFiscalMemoryService'

interface Props {
  memoryImports: FiscalMemoryImport[]
  products: AccountingProduct[]
  spedDocuments: StoredDocument[]
  isOpen: boolean
  onClose: () => void
  onLinked: () => void
}

type Step = 'select' | 'preview' | 'result'

export function FiscalMemoryXmlLinkModal({
  memoryImports,
  products,
  spedDocuments,
  isOpen,
  onClose,
  onLinked,
}: Props) {
  const { t, locale } = useTranslation()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const temporaryBatchIds = useRef<Set<string>>(new Set())
  const [step, setStep] = useState<Step>('select')
  const [available, setAvailable] = useState<StoredDocument[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [previews, setPreviews] = useState<XmlMemoryPreview[]>([])
  const [result, setResult] = useState<XmlMemoryLinkResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<DocumentUploadProgress | null>(null)
  const [ignoredUploadCount, setIgnoredUploadCount] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const targetImports = (['sped_fiscal', 'sped_contributions'] as const).flatMap((sourceKind) => {
    const match = memoryImports.find((item) => item.source_kind === sourceKind)
    return match ? [match] : []
  })
  const memoryImport = targetImports[0] || null

  useEffect(() => () => {
    const batchIds = Array.from(temporaryBatchIds.current)
    temporaryBatchIds.current.clear()
    void Promise.allSettled(batchIds.map(discardFiscalMemoryUpload))
  }, [])

  const suggested = useMemo(
    () => memoryImport ? xmlDocumentsForPeriod(available, memoryImport) : [],
    [available, memoryImport],
  )
  useEffect(() => {
    if (!isOpen || !memoryImport) return
    setStep('select')
    setAvailable([])
    setSelectedIds(new Set())
    setPreviews([])
    setResult(null)
    setBusy(false)
    setUploadProgress(null)
    setIgnoredUploadCount(0)
    setError(null)
  }, [isOpen, memoryImport?.id])

  const preview = useMemo(() => {
    if (previews.length === 0) return null
    const primary = previews.find((item) => item.memoryImport.source_kind === 'sped_fiscal') || previews[0]
    const unmatched = new Map<string, XmlMemoryPreview['unmatchedDocuments'][number]>()
    previews.forEach((item) => item.unmatchedDocuments.forEach((entry) => unmatched.set(entry.document.id, entry)))
    return {
      ...primary,
      items: previews.flatMap((item) => item.items),
      matchedDocuments: Math.max(...previews.map((item) => item.matchedDocuments), 0),
      expectedDocuments: Math.max(...previews.map((item) => item.expectedDocuments), 0),
      unmatchedDocuments: Array.from(unmatched.values()),
      ignoredOutsidePeriod: Math.max(...previews.map((item) => item.ignoredOutsidePeriod), 0),
    }
  }, [previews])

  const formatDate = (value: string | null) => value
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(new Date(`${value.slice(0, 10)}T12:00:00`))
    : '—'
  const translatedError = (value: unknown) => {
    const code = String(value instanceof Error ? value.message : (value as any)?.message || '').trim()
    if (/no unique or exclusion constraint matching the ON CONFLICT specification/i.test(code)) {
      return t('accounting.fiscalMemory.errors.databaseUpdateRequired')
    }
    if (code && !/^[a-z][a-z0-9_]*$/i.test(code)) {
      return t('accounting.fiscalMemory.errors.xmlLinkFailed')
    }
    const translated = t(`accounting.fiscalMemory.errors.${code || 'xmlLinkFailed'}`)
    return translated.startsWith('accounting.') ? t('accounting.fiscalMemory.errors.xmlLinkFailed') : translated
  }

  const cleanupTemporaryUploads = async () => {
    const batchIds = Array.from(temporaryBatchIds.current)
    temporaryBatchIds.current.clear()
    const results = await Promise.allSettled(batchIds.map(discardFiscalMemoryUpload))
    results.filter((result) => result.status === 'rejected').forEach((result) => {
      console.error('[FiscalMemoryXmlLinkModal] Temporary file cleanup error:', result.reason)
    })
  }

  const handleClose = async () => {
    if (busy) return
    await cleanupTemporaryUploads()
    onClose()
  }

  const handleFiles = async (files: File[]) => {
    if (!memoryImport || files.length === 0 || busy) return
    setBusy(true)
    setUploadProgress({ current: 0, total: files.length, fileName: files[0]?.name || '' })
    setError(null)
    try {
      const loaded = await uploadXmlMemoryDocuments(memoryImport.unit_id, files, setUploadProgress)
      temporaryBatchIds.current.add(loaded.result.batch.id)
      const uploadedBatchDocuments = loaded.documents
        .filter((document) => document.batch_id === loaded.result.batch.id)
      const uploadedDocuments = xmlDocumentsForPeriod(uploadedBatchDocuments, memoryImport)
      const outsidePeriodCount = uploadedBatchDocuments.length - uploadedDocuments.length
      setAvailable((current) => [
        ...uploadedDocuments,
        ...current.filter((document) => !uploadedDocuments.some((uploaded) => uploaded.id === document.id)),
      ])
      setSelectedIds(new Set(uploadedDocuments.map((document) => document.id)))
      setIgnoredUploadCount((current) => current + outsidePeriodCount)
      if (uploadedDocuments.length === 0) {
        setError(t('accounting.fiscalMemory.errors.uploadedXmlNotFound'))
      }
    } catch (uploadError) {
      console.error('[FiscalMemoryXmlLinkModal] Upload error:', uploadError)
      setError(translatedError(uploadError))
    } finally {
      setBusy(false)
      setUploadProgress(null)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const clearDocumentList = () => {
    setAvailable([])
    setSelectedIds(new Set())
    setError(null)
  }
  const progressPercent = uploadProgress && uploadProgress.total > 0
    ? Math.round((uploadProgress.current / uploadProgress.total) * 100)
    : 0

  const handleSubmit = async () => {
    if (!memoryImport || busy) return
    if (step === 'select') {
      const selected = available.filter((document) => selectedIds.has(document.id))
      if (selected.length === 0) { setError(t('accounting.fiscalMemory.errors.selectXml')); return }
      setBusy(true)
      setError(null)
      try {
        const prepared = [] as XmlMemoryPreview[]
        for (const targetImport of targetImports) {
          const targetSped = spedDocuments.find((document) => document.id === targetImport.source_document_id) || null
          prepared.push(await prepareXmlMemoryPreview(targetImport, targetSped, selected, products))
        }
        setPreviews(prepared)
        setStep('preview')
      } catch (previewError) {
        console.error('[FiscalMemoryXmlLinkModal] Preview error:', previewError)
        setError(translatedError(previewError))
      } finally { setBusy(false) }
      return
    }
    if (step !== 'preview' || previews.length === 0 || previews.every((item) => item.items.length === 0)) return
    setBusy(true)
    setError(null)
    try {
      const linkedResults: XmlMemoryLinkResult[] = []
      for (const prepared of previews) {
        if (prepared.items.length > 0) linkedResults.push(await linkXmlMemory(prepared))
      }
      const linked = linkedResults.reduce<XmlMemoryLinkResult>((total, current) => ({
        processed_items: total.processed_items + current.processed_items,
        created_parties: total.created_parties + current.created_parties,
        linked_codes: total.linked_codes + current.linked_codes,
        operation_evidence: total.operation_evidence + current.operation_evidence,
        tax_evidence: total.tax_evidence + current.tax_evidence,
      }), { processed_items: 0, created_parties: 0, linked_codes: 0, operation_evidence: 0, tax_evidence: 0 })
      await cleanupTemporaryUploads()
      setResult(linked)
      setStep('result')
      onLinked()
    } catch (linkError) {
      console.error('[FiscalMemoryXmlLinkModal] Link error:', linkError)
      setError(translatedError(linkError))
    } finally { setBusy(false) }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => void handleClose()}
      title={t('accounting.fiscalMemory.xmlLink.title')}
      size="lg"
      onSubmit={step === 'result' || (step === 'preview' && previews.every((item) => item.items.length === 0)) ? undefined : handleSubmit}
      submitLabel={t(step === 'select' ? 'accounting.fiscalMemory.xmlLink.analyze' : 'accounting.fiscalMemory.xmlLink.confirm')}
      submitting={busy}
      closeLabel={t('actions.close')}
    >
      <div className="space-y-5">
        <p className="text-xs text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.xmlLink.temporaryFileHint')}</p>
        <div className="flex items-center gap-2 text-xs font-medium">
          {(['select', 'preview', 'result'] as const).map((item, index) => (
            <div key={item} className="flex flex-1 items-center gap-2">
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${step === item || (step === 'result' && item !== 'result') || (step === 'preview' && item === 'select') ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300'}`}>{index + 1}</span>
              <span className="hidden text-slate-600 dark:text-slate-300 sm:inline">{t(`accounting.fiscalMemory.xmlLink.steps.${item}`)}</span>
              {index < 2 && <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />}
            </div>
          ))}
        </div>

        {error && <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300">{error}</div>}

        {busy && (
          <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950/30">
            <div className="flex items-center gap-3">
              <svg className="h-6 w-6 shrink-0 animate-spin text-emerald-600 dark:text-emerald-300" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" /><path className="opacity-90" fill="currentColor" d="M21 12a9 9 0 0 0-9-9v3a6 6 0 0 1 6 6h3Z" /></svg>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-emerald-900 dark:text-emerald-100">{t(uploadProgress ? 'accounting.fiscalMemory.xmlLink.processingFiles' : step === 'preview' ? 'accounting.fiscalMemory.xmlLink.savingMemory' : 'accounting.fiscalMemory.xmlLink.analyzingFiles')}</p>
                <p className="mt-1 truncate text-xs text-emerald-700 dark:text-emerald-300">{uploadProgress
                  ? t('accounting.fiscalMemory.xmlLink.processingProgress', {
                    current: String(uploadProgress.current),
                    total: String(uploadProgress.total),
                    file: uploadProgress.fileName,
                  })
                  : t('accounting.fiscalMemory.xmlLink.processingHint')}</p>
              </div>
              {uploadProgress && <span className="text-sm font-bold text-emerald-800 dark:text-emerald-200">{progressPercent}%</span>}
            </div>
            {uploadProgress && <div className="mt-3 h-2 overflow-hidden rounded-full bg-emerald-100 dark:bg-emerald-900"><div className="h-full rounded-full bg-emerald-600 transition-all duration-300" style={{ width: `${progressPercent}%` }} /></div>}
          </section>
        )}

        {memoryImport && step === 'select' && (
          <>
            <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950/30">
              <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">{memoryImport.source_document_name}</p>
              <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">{formatDate(memoryImport.period_start)} – {formatDate(memoryImport.period_end)} · {t('accounting.fiscalMemory.xmlLink.samePeriodHint')}</p>
            </section>
            {ignoredUploadCount > 0 && <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">{t('accounting.fiscalMemory.capture.xml.ignoredOutsidePeriod', { count: String(ignoredUploadCount) })}</p>}
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{t('accounting.fiscalMemory.xmlLink.available', { count: String(suggested.length) })}</p>
              <div className="flex flex-wrap items-center justify-end gap-3">
                {suggested.length > 0 && <button type="button" onClick={clearDocumentList} className="text-xs font-semibold text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white">{t('accounting.fiscalMemory.xmlLink.clearList')}</button>}
                {suggested.length > 0 && <button type="button" onClick={() => setSelectedIds(new Set(suggested.map((document) => document.id)))} className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">{t('accounting.fiscalMemory.xmlLink.selectAll')}</button>}
              </div>
            </div>
            <div className="max-h-72 divide-y divide-slate-100 overflow-auto rounded-xl border border-slate-200 dark:divide-slate-700 dark:border-slate-700">
              {suggested.length === 0 && !busy && <p className="p-5 text-center text-sm text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.xmlLink.cleanListHint')}</p>}
              {suggested.length === 0 && busy && <p className="p-5 text-center text-sm font-medium text-emerald-700 dark:text-emerald-300">{t('accounting.fiscalMemory.xmlLink.processingFiles')}</p>}
              {suggested.map((document) => (
                <label key={document.id} className="flex cursor-pointer items-start gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-700/40">
                  <input type="checkbox" checked={selectedIds.has(document.id)} onChange={(event) => setSelectedIds((current) => { const next = new Set(current); if (event.target.checked) next.add(document.id); else next.delete(document.id); return next })} className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                  <span className="min-w-0"><span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-200">{document.original_name}</span><span className="mt-0.5 block text-xs text-slate-500">{String((document.metadata?.summary as any)?.issued_at || '').slice(0, 10) || t('accounting.fiscalMemory.xmlLink.dateReadDuringAnalysis')}</span></span>
                </label>
              ))}
            </div>
            <input ref={inputRef} type="file" multiple accept=".xml,.zip,application/xml,text/xml,application/zip" className="hidden" onChange={(event) => void handleFiles(Array.from(event.target.files || []))} />
            <button type="button" disabled={busy} onClick={() => inputRef.current?.click()} className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 px-4 py-5 text-sm font-semibold text-emerald-700 hover:border-emerald-500 hover:bg-emerald-50 disabled:opacity-50 dark:border-slate-700 dark:text-emerald-300 dark:hover:bg-emerald-950/20">
              {busy ? <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" /><path fill="currentColor" d="M21 12a9 9 0 0 0-9-9v3a6 6 0 0 1 6 6h3Z" /></svg> : <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" /></svg>}
              {t(busy ? 'accounting.fiscalMemory.xmlLink.processingFiles' : 'accounting.fiscalMemory.xmlLink.upload')}
            </button>
          </>
        )}

        {step === 'preview' && preview && (
          <>
            <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <article className="rounded-xl border border-slate-200 p-3 dark:border-slate-700"><p className="text-xs text-slate-500">{t('accounting.fiscalMemory.xmlLink.counts.selected')}</p><p className="mt-1 text-xl font-semibold text-slate-900 dark:text-white">{preview.selectedDocuments.length}</p></article>
              <article className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950/20"><p className="text-xs text-emerald-700 dark:text-emerald-300">{t('accounting.fiscalMemory.xmlLink.counts.matched')}</p><p className="mt-1 text-xl font-semibold text-emerald-800 dark:text-emerald-200">{preview.matchedDocuments} / {preview.expectedDocuments}</p></article>
              <article className="rounded-xl border border-slate-200 p-3 dark:border-slate-700"><p className="text-xs text-slate-500">{t('accounting.fiscalMemory.xmlLink.counts.items')}</p><p className="mt-1 text-xl font-semibold text-slate-900 dark:text-white">{preview.items.length}</p></article>
              <article className="rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/20"><p className="text-xs text-amber-700 dark:text-amber-300">{t('accounting.fiscalMemory.xmlLink.counts.review')}</p><p className="mt-1 text-xl font-semibold text-amber-800 dark:text-amber-200">{preview.unmatchedDocuments.length}</p></article>
            </section>
            <div className="rounded-xl border border-slate-200 p-4 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">{t('accounting.fiscalMemory.xmlLink.previewHint')}</div>
            {preview.unmatchedDocuments.length > 0 && (
              <section className="rounded-xl border border-amber-200 dark:border-amber-900">
                <h3 className="border-b border-amber-200 px-4 py-3 text-sm font-semibold text-amber-800 dark:border-amber-900 dark:text-amber-200">{t('accounting.fiscalMemory.xmlLink.reviewTitle')}</h3>
                <div className="max-h-52 divide-y divide-amber-100 overflow-auto dark:divide-amber-900/60">{preview.unmatchedDocuments.slice(0, 50).map(({ document, reason }) => <div key={document.id} className="flex justify-between gap-3 px-4 py-2 text-xs"><span className="truncate text-slate-700 dark:text-slate-300">{document.original_name}</span><span className="shrink-0 text-amber-700 dark:text-amber-300">{t(`accounting.fiscalMemory.xmlLink.reasons.${reason}`)}</span></div>)}</div>
              </section>
            )}
            {preview.items.length === 0 && <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300">{t('accounting.fiscalMemory.xmlLink.noMatches')}</div>}
          </>
        )}

        {step === 'result' && result && (
          <div className="py-6 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"><svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m5 13 4 4L19 7" /></svg></div>
            <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">{t('accounting.fiscalMemory.xmlLink.completed')}</h3>
            <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.xmlLink.completedSummary', { items: String(result.processed_items), codes: String(result.linked_codes), taxes: String(result.tax_evidence) })}</p>
            <button type="button" onClick={onClose} className="mt-5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">{t('actions.close')}</button>
          </div>
        )}
      </div>
    </Modal>
  )
}
